'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/', key: 'nav-home' },
  { label: 'Fleet', href: '#fleet', key: 'nav-fleet' },
  { label: 'Delhi Cab', href: '#fleet', key: 'nav-delhi' },
  { label: 'Outstation', href: '#destinations', key: 'nav-outstation' },
  { label: 'Destinations', href: '#destinations', key: 'nav-destinations' },
  { label: 'Reviews', href: '#reviews', key: 'nav-reviews' },
  { label: 'Gallery', href: '#gallery', key: 'nav-gallery' },
  { label: 'Contact', href: '#contact', key: 'nav-contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsOpen(false);
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass-nav py-3' : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3">
            <div
              className="relative flex-shrink-0 transition-transform duration-150 active:scale-90 cursor-pointer select-none"
              style={{ width: 44, height: 44 }}
            >
              <img
                src="/assets/images/IMG-20260511-WA0004-1778500311790-1778592884049.jpg"
                alt="Navya Tour and Travels Logo"
                className="w-full h-full rounded-full object-cover"
                style={{ mixBlendMode: 'normal' }}
              />
            </div>
            <div className="hidden sm:block">
              <span className="text-lg font-bold text-white leading-tight block">NAVYA</span>
              <span className="text-xs text-primary font-medium tracking-widest uppercase">Tour & Travels</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith('#')) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }
                }}
                className="nav-link"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+918860044910"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
            >
              <Phone size={14} className="text-primary" />
              <span>+91 88600 44910</span>
            </a>
            <a
              href="https://wa.me/918860044910?text=Hi%20Navya%20Travels%2C%20I%20want%20to%20book%20a%20cab"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary px-4 py-2 text-sm rounded-lg"
            >
              Book Now
            </a>
            <Link
              href="/admin-dashboard"
              className="btn-ghost px-4 py-2 text-sm rounded-lg"
            >
              Admin
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2 rounded-lg glass-card"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={() => setIsOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-72 bg-card border-l border-border/30 transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6 pt-20 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={(e) => {
                  if (link.href.startsWith('#')) {
                    e.preventDefault();
                    handleNavClick(link.href);
                  } else {
                    setIsOpen(false);
                  }
                }}
                className="py-3 px-4 text-muted-foreground hover:text-white hover:bg-primary/10 rounded-lg transition-all font-medium"
              >
                {link.label}
              </a>
            ))}
            <div className="mt-4 pt-4 border-t border-border/30 flex flex-col gap-3">
              <a
                href="tel:+918860044910"
                className="btn-primary py-3 text-center rounded-lg font-semibold text-sm"
              >
                📞 Call Now
              </a>
              <a
                href="https://wa.me/918860044910?text=Hi%20Navya%20Travels%2C%20I%20want%20to%20book%20a%20cab"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost py-3 text-center rounded-lg font-semibold text-sm"
              >
                WhatsApp Book
              </a>
              <Link
                href="/admin-dashboard"
                onClick={() => setIsOpen(false)}
                className="py-3 text-center text-muted-foreground hover:text-white text-sm transition-colors"
              >
                Admin Panel →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}